import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@workspace/strings/web";

export function KioskExitPinDialog({
  open,
  title,
  description,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: string;
  pending?: boolean;
  error?: string | null;
  onConfirm: (pin: string) => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (!open) setPin("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
    >
      <DialogContent data-testid="dialog-exit-pin">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="exit-pin">{t("webMisc.exitPinDialog.label")}</Label>
          <Input
            id="exit-pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t("webMisc.exitPinDialog.placeholder")}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            disabled={pending}
            data-testid="input-exit-pin"
            onKeyDown={(e) => {
              if (e.key === "Enter" && pin.trim() && !pending) {
                onConfirm(pin.trim());
              }
            }}
          />
          {error ? (
            <p className="text-sm text-destructive" data-testid="text-exit-pin-error">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            {t("common.action.cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(pin.trim())}
            disabled={!pin.trim() || pending}
            data-testid="button-exit-pin-confirm"
          >
            {pending ? t("webMisc.exitPinDialog.verifying") : t("common.action.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
